import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import ProductCard from './ProductCard'
import { categoryBadge, decorateProduct } from './productsData'
import { useToast } from '../../../context/ToastContext'
import { useRevealClass } from '../../../hooks/useRevealClass'

const GAP = 20
const AUTOPLAY_MS = 4200

function getCardsPerView() {
  if (typeof window === 'undefined') return 4
  if (window.innerWidth >= 1024) return 4
  if (window.innerWidth >= 640) return 2
  return 1
}

function SkeletonProductCard() {
  return (
    <article className="ps-card animate-pulse" aria-hidden="true">
      <div className="ps-img-wrap" style={{ background: 'rgba(200,146,42,.10)' }} />
      <div className="ps-body space-y-3">
        <div className="h-3.5 w-3/4 rounded" style={{ background: 'rgba(200,146,42,.14)' }} />
        <div className="h-2.5 w-full rounded" style={{ background: 'rgba(200,146,42,.08)' }} />
        <div className="h-2.5 w-5/6 rounded" style={{ background: 'rgba(200,146,42,.08)' }} />
        <div className="h-6 w-1/3 rounded" style={{ background: 'rgba(200,146,42,.12)' }} />
        <div className="h-9 w-full rounded-lg" style={{ background: 'rgba(200,146,42,.10)' }} />
      </div>
    </article>
  )
}

export default function FeaturedProductsSlider() {
  const { showToast } = useToast()
  const tabsReveal = useRevealClass()
  const sliderReveal = useRevealClass()
  const [filter, setFilter] = useState('all')
  const [current, setCurrent] = useState(0)
  const [cardsPerView, setCardsPerView] = useState(getCardsPerView)
  const [stepPx, setStepPx] = useState(0)

  const trackRef = useRef(null)
  const viewportRef = useRef(null)
  const firstCardRef = useRef(null)
  const timerRef = useRef(null)

  // Featured products — upstream paginates at 8 per page, ideal teaser size.
  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['featured-products'],
    queryFn: () => api.catalog.featured(),
  })

  const products = useMemo(() => (data?.data?.data ?? []).map(decorateProduct), [data])

  // Filter tabs come from the categories actually present in the payload.
  const filterTabs = useMemo(() => {
    const names = [...new Set(products.map((p) => p.category))]
    return [
      { key: 'all', label: 'All' },
      ...names.map((name) => ({ key: name, label: `${categoryBadge(name).emoji} ${name}` })),
    ]
  }, [products])

  const filtered = useMemo(
    () => products.filter((p) => filter === 'all' || p.category === filter),
    [products, filter],
  )
  const maxIndex = Math.max(0, filtered.length - cardsPerView)

  useEffect(() => {
    setCurrent((c) => Math.max(0, Math.min(c, maxIndex)))
  }, [maxIndex])

  useLayoutEffect(() => {
    const measure = () => {
      if (firstCardRef.current) {
        setStepPx(firstCardRef.current.getBoundingClientRect().width + GAP)
      }
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [filtered, cardsPerView])

  useEffect(() => {
    const onResize = () => setCardsPerView(getCardsPerView())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const stop = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const start = useCallback(() => {
    stop()
    timerRef.current = window.setInterval(() => {
      setCurrent((c) => (c >= maxIndex ? 0 : c + 1))
    }, AUTOPLAY_MS)
  }, [stop, maxIndex])

  useEffect(() => {
    start()
    return stop
  }, [start, stop])

  const goTo = (n) => setCurrent(Math.max(0, Math.min(maxIndex, n)))

  const slide = (dir) => {
    stop()
    goTo(current + dir)
    start()
  }

  const selectFilter = (key) => {
    setFilter(key)
    setCurrent(0)
    stop()
    start()
  }

  const handleMouseDown = (e) => {
    const startCurrent = current
    const startX = e.clientX
    let dragDelta = 0
    if (trackRef.current) trackRef.current.style.transition = 'none'
    stop()

    const onMove = (ev) => {
      dragDelta = ev.clientX - startX
      if (trackRef.current) {
        trackRef.current.style.transform = `translateX(${-(startCurrent * stepPx) + dragDelta}px)`
      }
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      if (trackRef.current) trackRef.current.style.transition = ''
      if (Math.abs(dragDelta) > 60) {
        goTo(startCurrent + (dragDelta < 0 ? 1 : -1))
      } else if (trackRef.current) {
        trackRef.current.style.transform = `translateX(${-(startCurrent * stepPx)}px)`
      }
      start()
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  const touchStartRef = useRef(0)
  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX
  }
  const handleTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchStartRef.current
    if (Math.abs(dx) > 40) slide(dx < 0 ? 1 : -1)
  }

  return (
    <>
      <div
        ref={tabsReveal.ref}
        className={`ps-reveal flex flex-wrap gap-2 mb-8${tabsReveal.visible ? ' ps-visible' : ''}`}
        id="ps-tabs"
        style={{ transitionDelay: '.08s' }}
      >
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`ps-tab-btn${filter === tab.key ? ' ps-tab-active' : ''}`}
            onClick={() => selectFilter(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Reveal container stays mounted so useRevealClass can observe it from
          first render — pending/error/content swap happens inside. */}
      <div
        ref={sliderReveal.ref}
        className={`ps-reveal${sliderReveal.visible ? ' ps-visible' : ''}`}
        style={{ position: 'relative', padding: '0 4px', transitionDelay: '.14s' }}
        onMouseEnter={stop}
        onMouseLeave={start}
      >
        {isError && (
          <div className="py-14 text-center">
            <div className="text-5xl mb-4 opacity-40">🛰️</div>
            <p className="font-royal text-lg font-bold mb-1" style={{ color: '#1C0A03' }}>
              Couldn’t Load Products
            </p>
            <p className="text-sm mb-5" style={{ color: '#7A5C38' }}>
              Something went wrong while fetching live data. Please try again.
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
              style={{ borderColor: 'rgba(200,146,42,.35)', color: '#C8922A', background: 'rgba(200,146,42,.06)' }}
            >
              Retry
            </button>
          </div>
        )}

        {isPending && (
          <div style={{ display: 'flex', gap: `${GAP}px`, overflow: 'hidden' }}>
            {Array.from({ length: getCardsPerView() }, (_, i) => (
              <SkeletonProductCard key={i} />
            ))}
          </div>
        )}

        {!isPending && !isError && (
          <>
        <button type="button" id="ps-prev" className="ps-arrow ps-arrow-prev" onClick={() => slide(-1)} disabled={current === 0} aria-label="Previous products">
          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button type="button" id="ps-next" className="ps-arrow ps-arrow-next" onClick={() => slide(1)} disabled={current >= maxIndex} aria-label="Next products">
          <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        <div id="ps-viewport" ref={viewportRef} style={{ overflow: 'hidden', borderRadius: '6px' }} onMouseDown={handleMouseDown} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          <div
            id="ps-track"
            ref={trackRef}
            style={{ display: 'flex', gap: `${GAP}px`, transform: `translateX(${-(current * stepPx)}px)`, transition: 'transform .55s cubic-bezier(.16,1,.3,1)', willChange: 'transform', cursor: 'grab' }}
          >
            {filtered.map((product, i) => (
              <ProductCard key={product.id} product={product} ref={i === 0 ? firstCardRef : undefined} onView={() => showToast(`Opening details for ${product.name}…`)} />
            ))}
          </div>
        </div>

        <div id="ps-dots" role="tablist" aria-label="Product slider pages" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '28px' }}>
          {Array.from({ length: maxIndex + 1 }, (_, i) => (
            <button
              key={i}
              type="button"
              className={`ps-pagination-dot${i === current ? ' ps-dot-active' : ''}`}
              onClick={() => {
                stop()
                goTo(i)
                start()
              }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
          </>
        )}
      </div>
    </>
  )
}
